/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

// Chakra imports
import {
    Box,
    Icon,
    IconButton,
    Spinner,
    SimpleGrid,
    useDisclosure,
    ModalOverlay,
    FormControl,
    FormLabel,
    FormErrorMessage,
    useColorModeValue,
    Input,
    Textarea,
    Button,
    Modal,
    ModalBody,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    Text,
    Flex,
    Center,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Select,
    useToast,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
    MdPerson,
    MdArrowCircleLeft,
    MdAddCircleOutline,
} from "react-icons/md";
import { DeleteIcon, InfoIcon } from "@chakra-ui/icons";
import { useParams, useNavigate } from "react-router-dom";
import {
    useCreatePatientLabMutation,
    useDeletePatientLabMutation,
    useGetLabsByPatientIdQuery,
    useGetCurrentUserQuery,
    useGetUsersQuery,
} from "store";
import { Helmet } from "react-helmet-async";
import moment from "moment";
import PageTitle from "components/title";

function PaginatedTable({ columns, initialPageSize = 10 }) {
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const { patientId } = useParams();

    const { data: labs, isFetching, error } = useGetLabsByPatientIdQuery(
        { patientId, page: pageIndex + 1, pageSize },
        { keepPreviousData: true }
    );

    // Safely handle undefined labs and default to last page as 0
    const lastPage =
        labs && labs.count ? Math.ceil(labs.count / pageSize) - 1 : 0;
    const currentPage = pageIndex + 1;

    // if (error) return <Text color="red.500">Error: {error.message}</Text>;
    if (isFetching) return <Spinner size="lg" />;

    return (
        <Box overflowX="auto">
            <Table variant="striped" colorScheme="teal">
                <Thead>
                    <Tr>
                        {columns.map((column) => (
                            <Th key={column.id}>{column.header}</Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {labs && labs.results && labs.results.length > 0 ? (
                        labs.results.map((row, i) => (
                            <Tr key={i}>
                                {columns.map((column) => (
                                    <Td key={column.id}>
                                        {typeof column.accessor === "function"
                                            ? column.accessor(row)
                                            : row[column.accessor]}
                                    </Td>
                                ))}
                            </Tr>
                        ))
                    ) : (
                        <Tr>
                            <Td colSpan={columns.length} textAlign="center">
                                No labs available
                            </Td>
                        </Tr>
                    )}
                </Tbody>
            </Table>
            <Flex justifyContent="space-between" alignItems="center" p="4">
                <Button
                    onClick={() => setPageIndex(0)}
                    disabled={pageIndex === 0}
                >
                    {"<<"}
                </Button>
                <Button
                    onClick={() => setPageIndex((old) => Math.max(old - 1, 0))}
                    disabled={pageIndex === 0}
                >
                    {"<"}
                </Button>
                <Text>
                    Page {currentPage} of {lastPage + 1}
                </Text>
                <span>
                    | Go to page:{" "}
                    <Input
                        type="number"
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value
                                ? Number(e.target.value) - 1
                                : 0;
                            setPageIndex(page);
                        }}
                        style={{ width: "100px" }}
                    />
                </span>
                <Button
                    onClick={() =>
                        setPageIndex((old) => Math.min(old + 1, lastPage))
                    }
                    disabled={pageIndex >= lastPage}
                >
                    {">"}
                </Button>
                <Button
                    onClick={() => setPageIndex(lastPage)}
                    disabled={pageIndex >= lastPage}
                >
                    {">>"}
                </Button>
                <Select
                    ml="4"
                    width="auto"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPageIndex(0); // Reset to the first page after changing page size
                    }}
                >
                    {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </Select>
            </Flex>
        </Box>
    );
}

function Labs() {
    const { patientId } = useParams();
    const { data: labs, error, isLoading } = useGetLabsByPatientIdQuery({
        patientId,
        page: 1,
        pageSize: 10,
    });
    const labsCount = labs ? labs.count : 0;
    const {
        data: currentUser,
        error: err,
        isLoading: isFetching,
    } = useGetCurrentUserQuery();
    const [labToDelete, setLabToDelete] = useState(null);
    const [
        deleteLab,
        { isLoading: isDeleting },
    ] = useDeletePatientLabMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const toast = useToast();

    const handleDetailsClick = (labId) => {
        // console.log("Visit ID for detail:", labId);
        navigate(`${labId}/details`);
    };

    const handleDeleteClick = (labId) => {
        setLabToDelete(labId);
        onOpen();
    };

    const confirmDelete = async () => {
        try {
            if (!labToDelete) return;

            await deleteLab({ patientId, labId: labToDelete }).unwrap();

            toast({
                title: "Lab record deleted",
                description: "The lab entry has been removed.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            setLabToDelete(null);
            onClose();
        } catch (error) {
            console.error("Error deleting lab:", error);
            toast({
                title: "Error deleting lab",
                description:
                    error?.data?.detail ||
                    error?.error ||
                    "Could not delete lab entry.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const [createLab, results] = useCreatePatientLabMutation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
    } = useForm();

    React.useEffect(() => {
        setValue("patient", patientId);
    }, [patientId, setValue]);

    const onSubmit = async (data) => {
        try {
            const payload = { ...data };
            await createLab({ patientId, newPatientLab: payload }).unwrap();

            toast({
                title: "Lab record created",
                description: "Your new lab entry has been saved.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            reset();
            handleClose();
        } catch (error) {
            toast({
                title: "Error",
                description:
                    error?.data?.detail ||
                    error?.error ||
                    "Failed to create lab record.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleBacktoPatientDetails = () => {
        navigate(`/patients/${patientId}/details`);
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // useEffect(() => {
    //     if (isFetching) {
    //         console.log("Loading current user data...");
    //     }

    //     if (err) {
    //         console.error("Error fetching current user:", err);
    //     }

    //     if (currentUser) {
    //         console.log("Current User:", currentUser);
    //         console.log("Current User ID:", currentUser.id);
    //     }
    // }, [currentUser, err, isFetching]);

    // Fetch users data
    const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

    // Log users data for debugging
    // console.log("Fetched users:", users);

    const userMap = useMemo(() => {
        const map = {};
        if (users && users.results) {
            // Make sure you access the results array correctly.
            users.results.forEach((user) => {
                if (user.id && user.username) {
                    map[user.id] = user.username;
                }
            });
        }
        return map;
    }, [users]); // Make sure 'users' contains the fetched data array.

    // console.log("User Map:", userMap);

    const columns = useMemo(
        () => [
            {
                id: "date",
                header: "Date",
                accessor: (row) => moment(row.date).format("MMMM D, YYYY"), // Assuming row.date is the correct path
            },
            {
                id: "type",
                header: "Type of Lab",
                accessor: (row) => row.type, // Directly accessing the reason
            },
            {
                id: "requested_by",
                header: "Requested By",
                accessor: (row) => {
                    const username = userMap[String(row.requested_by)]; // Accessing from userMap directly
                    return username || "Unknown User";
                },
            },
            {
                id: "actions",
                header: "Actions",
                accessor: (row) => (
                    <div>
                        <IconButton
                            aria-label="View details"
                            icon={<InfoIcon />}
                            onClick={() => handleDetailsClick(row.id)}
                            mr={2}
                        />
                        <IconButton
                            aria-label="Delete"
                            icon={<DeleteIcon />}
                            onClick={() => handleDeleteClick(row.id)}
                            colorScheme="red"
                            isLoading={isDeleting}
                        />
                    </div>
                ),
            },
        ],
        [userMap, isDeleting]
    );

    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const [scrollBehavior, setScrollBehavior] = React.useState("inside");

    return (
        <>
            <PageTitle title="Patient Labs" />
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
                <SimpleGrid
                    columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                    gap="20px"
                    mb="20px"
                >
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w="56px"
                                h="56px"
                                bg={boxBg}
                                icon={
                                    <Icon
                                        w="32px"
                                        h="32px"
                                        as={MdPerson}
                                        color={brandColor}
                                    />
                                }
                            />
                        }
                        name="Total Labs"
                        value={
                            isLoading ? (
                                <Spinner size="lg" />
                            ) : err ? (
                                <Text>Error fetching labs</Text>
                            ) : (
                                labsCount
                            )
                        }
                    />
                </SimpleGrid>
                {/* Delete modal form */}
                <Modal isOpen={isOpen} onClose={onClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Deletion</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you want to delete this visit?
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={isDeleting}
                            >
                                Delete
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* End form */}

                <Flex justify="flex-end" mb="4">
                    <Button
                        leftIcon={<MdAddCircleOutline />}
                        isLoading={results.isLoading}
                        onClick={handleShow}
                    >
                        New Lab
                    </Button>

                    <Button
                        leftIcon={<MdArrowCircleLeft />}
                        ml="4"
                        onClick={handleBacktoPatientDetails}
                    >
                        Back to Patient Details
                    </Button>
                </Flex>

                {/* Create modal form */}

                <Modal
                    isOpen={show}
                    onClose={handleClose}
                    isCentered
                    {...labs}
                    closeOnOverlayClick={false}
                    closeOnEsc={false}
                    scrollBehavior={scrollBehavior}
                    size="4xl"
                >
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>New Lab</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Type */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Type</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("type")}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Specimen */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl>
                                            <FormLabel>Specimen</FormLabel>
                                            <Input
                                                type="text"
                                                {...register("specimen")}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Data */}
                                <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                    <Box>
                                        <FormControl isInvalid={errors.data}>
                                            <FormLabel>Clinical Data</FormLabel>
                                            <Textarea
                                                {...register("data", {
                                                    onChange: (e) => {
                                                        const capitalizedValue = capitalizeFirstLetter(
                                                            e.target.value
                                                        );
                                                        setValue(
                                                            "data",
                                                            capitalizedValue,
                                                            {
                                                                shouldValidate: true,
                                                            }
                                                        );
                                                    },
                                                })}
                                            />
                                        </FormControl>
                                    </Box>
                                </SimpleGrid>

                                {/* Hidden Patient Field */}
                                <Input
                                    type="hidden"
                                    {...register("patient")} // Ensure it's required
                                />

                                {/* Hidden Enter By Field */}
                                <Input
                                    value={currentUser?.id || ""}
                                    {...register("requested_by")}
                                    type="hidden"
                                />

                                <Center>
                                    <Box>
                                        <Button
                                            fontSize="sm"
                                            variant="outline"
                                            fontWeight="500"
                                            type="submit"
                                            h="50"
                                            mb="24px"
                                            isLoading={results.isLoading}
                                            loadingText="Loading..."
                                            spinner={<Spinner />}
                                        >
                                            Save
                                        </Button>
                                    </Box>
                                </Center>
                            </form>
                        </ModalBody>
                    </ModalContent>
                </Modal>

                {/* End form */}

                <SimpleGrid
                    columns={{ base: 1, md: 1, xl: 1 }}
                    gap="20px"
                    mb="20px"
                >
                    <PaginatedTable columns={columns} initialPageSize={10} />
                </SimpleGrid>
            </Box>
        </>
    );
}

export default Labs;
